import { ComponentType, forwardRef, ReactNode, Ref, useState } from 'react'
import { Tooltip } from 'antd'
import { TooltipPlacement } from 'antd/lib/tooltip'

interface WithTooltipProps<V = unknown> {
	showTooltip?: boolean
	placeholder?: ReactNode
	tooltipText?: ReactNode
	tooltipPlacement?: TooltipPlacement
	isTooltipWithText?: boolean
	value?: V
}

// React's `forwardRef` typings can't express a generic props type cleanly —
// `PropsWithoutRef<P>` doesn't structurally resolve back to `P` when `P` is an
// open type parameter, which TS then (correctly, per its own rules) flags.
// This thin wrapper re-types the result using the well-known "fixed forwardRef"
// pattern, so `withTooltip` itself stays fully generic over both `P` (the
// wrapped component's props) and `V` (its `value` type) for every call site.
const typedForwardRef = <T, P>(
	render: (props: P, ref: Ref<T>) => JSX.Element | null
): ((props: P & { ref?: Ref<T> }) => JSX.Element | null) =>
	forwardRef(render as (props: unknown, ref: Ref<T>) => JSX.Element | null) as unknown as (
		props: P & { ref?: Ref<T> }
	) => JSX.Element | null

export const withTooltip = <P extends {}, V = unknown>(Component: ComponentType<P>) =>
	typedForwardRef<unknown, P & WithTooltipProps<V>>(
		(
			{
				showTooltip = false,
				tooltipText,
				tooltipPlacement,
				isTooltipWithText,
				...props
			},
			ref
		) => {
			const [isOpenDropdown, setIsOpenDropdown] = useState<boolean>(false)

			// Truthiness check is intentional and pre-existing: several consumers pass
			// non-string values here (e.g. FormSwitch/Icon pass a boolean `checked`,
			// antd Select/DatePicker can pass `0`/undefined). Switching to a strict
			// `!== null && !== undefined && !== ''` check would flip tooltip suppression
			// for those `0`/`false` cases and change existing behavior, so we deliberately
			// keep the `!!props.value` check here.
			// For SearchAutocomplete specifically, `value` has already been passed
			// through `normalizeSearchText` upstream (see SearchAutocomplete.tsx), so by
			// the time it reaches this check it's always a plain string — a non-empty
			// display string (even "0") legitimately means "has content" for a text field.
			return showTooltip ? (
				<Tooltip
					title={tooltipText ?? props.placeholder}
					placement={tooltipPlacement ?? 'topLeft'}
					{...(isOpenDropdown || (!!props.value && !isTooltipWithText)
						? { open: false }
						: {})}
					children={
						<Component
							{...(props as unknown as P)}
							ref={ref}
							onDropdownVisibleChange={(open: boolean) => {
								setIsOpenDropdown(open)
							}}
						/>
					}
				/>
			) : (
				<Component {...(props as unknown as P)} ref={ref} />
			)
		}
	)

